import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMacroData, CSAlphaRegime } from "@/hooks/useMacroData";
import { useBTCHistory, BTCPeriod } from "@/hooks/useBTCHistory";
import { useSSRAlpha, DEFAULT_SSR_CONFIG, SSRConfig } from "@/hooks/useSSRAlpha";
import { getIndicatorInfo } from "@/content/indicators";
import { LineChart, TrendingUp, RefreshCw, Info } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  ResponsiveContainer,
  ComposedChart,
  AreaChart,
  BarChart,
  Bar,
  Cell,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Brush,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

type IndicatorType = 'nfci' | 'csAlpha' | 'ssrAlpha';

const PERIODS: BTCPeriod[] = ['1Y', '2Y', '3Y', '5Y', '10Y'];
const PERIOD_DAYS: Record<BTCPeriod, number> = {
  '1Y': 365,
  '2Y': 730,
  '3Y': 1095,
  '5Y': 1825,
  '10Y': 3650,
};

// Unified chart margins for perfect alignment
const CHART_MARGIN = { top: 10, right: 60, bottom: 0, left: 20 };
const HISTOGRAM_MARGIN = { top: 5, right: 60, bottom: 0, left: 20 };
const BRUSH_MARGIN = { top: 0, right: 60, bottom: 20, left: 20 };

// Get color for regime - NEVER return gray/black for neutral
function getRegimeColor(regime: string, opacity = 1): string {
  switch (regime) {
    case 'green':
      return opacity === 1 ? 'hsl(142, 76%, 36%)' : `rgba(34, 197, 94, ${opacity})`;
    case 'red':
      return opacity === 1 ? 'hsl(0, 84%, 60%)' : `rgba(239, 68, 68, ${opacity})`;
    default:
      // Neutral: transparent background - no colored zones for neutral regime
      return 'transparent';
  }
}

export default function MacroBTC() {
  const [period, setPeriod] = useState<BTCPeriod>('2Y');
  const [indicator, setIndicator] = useState<IndicatorType>('nfci');
  const [isForceRefreshing, setIsForceRefreshing] = useState(false);
  
  const { data: macroData, isLoading: macroLoading, forceRefresh } = useMacroData();
  const { data: btcData, isLoading: btcLoading, forceRefresh: forceRefreshBTC } = useBTCHistory(period);
  
  // SSR Alpha data - only fetch when SSR tab is active
  const ssrConfig: Partial<SSRConfig> = { 
    period: period === '1Y'
      ? '1Y'
      : period === '2Y'
        ? '2Y'
        : period === '3Y'
          ? '3Y'
          : '5Y'
  };
  const { data: ssrData, isLoading: ssrLoading, refetch: refetchSSR } = useSSRAlpha(ssrConfig);

  const isLoading = macroLoading || btcLoading || (indicator === 'ssrAlpha' && ssrLoading);

  // Get indicator description from content file
  const indicatorInfo = useMemo(() => {
    if (indicator === 'nfci') {
      return getIndicatorInfo('FinancialRiskAlpha');
    } else if (indicator === 'ssrAlpha') {
      return getIndicatorInfo('SSRAlpha');
    }
    return getIndicatorInfo('CreditSpreadAlpha');
  }, [indicator]);

  // Combine BTC and NFCI data for the chart
  const nfciChartData = useMemo(() => {
    if (!macroData?.financialRisk?.history || !btcData) return [];

    // Create BTC array sorted by timestamp for efficient lookup
    const btcSorted = btcData
      .map(d => ({ ts: new Date(d.date).getTime(), price: d.price, date: d.date }))
      .sort((a, b) => a.ts - b.ts);

    // Get NFCI history
    const nfciHistory = macroData.financialRisk.history;

    // Filter by period
    const now = new Date();
    const startDate = new Date(now.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
    const startTs = startDate.getTime();

    // Tolerance for date matching: 7 days (weekly data can be off by a few days)
    const TOLERANCE_MS = 7 * 24 * 60 * 60 * 1000;

    // Binary search to find closest BTC price
    const findClosestBTC = (targetTs: number): { price: number; date: string } | null => {
      if (btcSorted.length === 0) return null;
      
      let left = 0;
      let right = btcSorted.length - 1;
      
      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (btcSorted[mid].ts < targetTs) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }
      
      let closest = btcSorted[left];
      if (left > 0) {
        const prev = btcSorted[left - 1];
        if (Math.abs(prev.ts - targetTs) < Math.abs(closest.ts - targetTs)) {
          closest = prev;
        }
      }
      
      if (Math.abs(closest.ts - targetTs) <= TOLERANCE_MS) {
        return { price: closest.price, date: closest.date };
      }
      
      return null;
    };

    // Combine data - use NFCI as the base
    const combined = nfciHistory
      .filter(h => {
        const hTs = new Date(h.date).getTime();
        return hTs >= startTs && h.inverted !== null;
      })
      .map(h => {
        const hTs = new Date(h.date).getTime();
        const btcMatch = findClosestBTC(hTs);

        return {
          date: h.date,
          ts: hTs,
          indicatorValue: h.inverted,
          btcPrice: btcMatch?.price ?? null,
          regime: h.regime as 'green' | 'red' | 'neutral',
        };
      })
      .filter(d => d.btcPrice !== null)
      .sort((a, b) => a.ts - b.ts);

    return combined;
  }, [macroData, btcData, period]);

  // Combine BTC and CS Alpha data for the chart
  const csAlphaChartData = useMemo(() => {
    if (!macroData?.creditSpreadAlpha?.history || !btcData) return [];

    // Create BTC sorted array for binary search
    const btcSorted = btcData
      .map(d => ({ ts: new Date(d.date).getTime(), price: d.price, date: d.date }))
      .sort((a, b) => a.ts - b.ts);

    // Get CS Alpha history
    const csHistory = macroData.creditSpreadAlpha.history;

    // Filter by period
    const now = new Date();
    const startDate = new Date(now.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
    const startTs = startDate.getTime();

    // Tolerance for date matching: 3 days (daily data)
    const TOLERANCE_MS = 3 * 24 * 60 * 60 * 1000;

    const findClosestBTC = (targetTs: number): { price: number; date: string } | null => {
      if (btcSorted.length === 0) return null;
      
      let left = 0;
      let right = btcSorted.length - 1;
      
      while (left < right) {
        const mid = Math.floor((left + right) / 2);
        if (btcSorted[mid].ts < targetTs) {
          left = mid + 1;
        } else {
          right = mid;
        }
      }
      
      let closest = btcSorted[left];
      if (left > 0) {
        const prev = btcSorted[left - 1];
        if (Math.abs(prev.ts - targetTs) < Math.abs(closest.ts - targetTs)) {
          closest = prev;
        }
      }
      
      if (Math.abs(closest.ts - targetTs) <= TOLERANCE_MS) {
        return { price: closest.price, date: closest.date };
      }
      
      return null;
    };

    // Combine data - use CS Alpha as the base (daily)
    const combined = csHistory
      .filter(h => {
        const hTs = new Date(h.date).getTime();
        return hTs >= startTs;
      })
      .map(h => {
        const hTs = new Date(h.date).getTime();
        const btcMatch = findClosestBTC(hTs);

        return {
          date: h.date,
          ts: hTs,
          indicatorValue: h.value,
          sma: h.sma,
          upperThreshold: h.upperThreshold,
          btcPrice: btcMatch?.price ?? null,
          regime: h.regime as CSAlphaRegime,
        };
      })
      .filter(d => d.btcPrice !== null)
      .sort((a, b) => a.ts - b.ts);

    return combined;
  }, [macroData, btcData, period]);

  // Combine BTC and SSR Alpha data for the chart
  const ssrChartData = useMemo(() => {
    if (!ssrData?.history) return [];

    // Filter by period
    const now = new Date();
    const startDate = new Date(now.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
    const startTs = startDate.getTime();

    return ssrData.history
      .filter(h => h.date >= startTs)
      .map(h => ({
        date: new Date(h.date).toISOString().split('T')[0],
        ts: h.date,
        indicatorValue: h.ssrRsi,
        btcPrice: h.btcPrice,
        regime: h.ssrRsi !== null 
          ? (h.ssrRsi >= (ssrData.config?.rsiOverbought ?? 75) ? 'red' 
            : h.ssrRsi <= (ssrData.config?.rsiOversold ?? 35) ? 'green' 
            : 'neutral')
          : 'neutral' as 'green' | 'red' | 'neutral',
      }))
      .sort((a, b) => a.ts - b.ts);
  }, [ssrData, period]);

  // Select active chart data based on indicator
  const chartData = indicator === 'nfci' ? nfciChartData : indicator === 'csAlpha' ? csAlphaChartData : ssrChartData;

  // Calculate regime zones for background - using timestamps for time-based axis
  const regimeZones = useMemo(() => {
    if (chartData.length === 0) return [];
    
    const zones: Array<{ startTs: number; endTs: number; regime: string }> = [];
    let currentZone: { startTs: number; endTs: number; regime: string } | null = null;
    
    for (const point of chartData) {
      const regime = point.regime;
      
      // Only create zones for green or red (not neutral)
      if (regime === 'green' || regime === 'red') {
        if (!currentZone || currentZone.regime !== regime) {
          if (currentZone) zones.push(currentZone);
          currentZone = { startTs: point.ts, endTs: point.ts, regime };
        } else {
          currentZone.endTs = point.ts;
        }
      } else {
        // Neutral regime - close any open zone
        if (currentZone) {
          zones.push(currentZone);
          currentZone = null;
        }
      }
    }
    
    if (currentZone) zones.push(currentZone);
    return zones;
  }, [chartData]);

  // Current regime
  const currentRegime = useMemo(() => {
    if (indicator === 'nfci') {
      return macroData?.financialRisk?.regime ?? null;
    } else if (indicator === 'ssrAlpha') {
      return ssrData?.current?.signal === 'overbought' ? 'red' 
        : ssrData?.current?.signal === 'oversold' ? 'green' 
        : 'neutral';
    } else {
      return macroData?.creditSpreadAlpha?.regime ?? null;
    }
  }, [macroData, ssrData, indicator]);

  // Current indicator value from chartData
  const currentIndicatorValue = useMemo(() => {
    if (chartData.length === 0) return null;
    return chartData[chartData.length - 1].indicatorValue;
  }, [chartData]);

  const handleRefresh = async () => {
    setIsForceRefreshing(true);
    try {
      if (indicator === 'ssrAlpha') {
        await Promise.all([refetchSSR()]);
      } else {
        await Promise.all([forceRefresh(), forceRefreshBTC()]);
      }
    } finally {
      setIsForceRefreshing(false);
    }
  };

  // Calculate tick timestamps for X-axis based on period (calendar-based)
  const xAxisTicks = useMemo(() => {
    if (chartData.length === 0) return [];
    
    const ticks: number[] = [];
    const monthInterval = period === '1Y' ? 2 : period === '2Y' ? 3 : period === '3Y' ? 4 : period === '5Y' ? 6 : 12;
    
    const startDate = new Date(chartData[0].ts);
    const endDate = new Date(chartData[chartData.length - 1].ts);
    
    let current = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1);
    
    while (current <= endDate) {
      ticks.push(current.getTime());
      current = new Date(current.getFullYear(), current.getMonth() + monthInterval, 1);
    }
    
    return ticks;
  }, [chartData, period]);

  const refreshing = isLoading || isForceRefreshing;

  // Get regime label and badge variant
  const getRegimeBadge = () => {
    if (!currentRegime) return null;
    
    if (indicator === 'nfci') {
      return (
        <Badge 
          variant={currentRegime === 'green' ? 'default' : 'destructive'}
          className={currentRegime === 'green' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          {currentRegime === 'green' ? 'RELAXAMENTO' : 'APERTO'}
        </Badge>
      );
    } else if (indicator === 'ssrAlpha') {
      const labels: Record<string, string> = {
        green: 'SOBREVENDIDO',
        red: 'SOBRECOMPRADO',
        neutral: 'NEUTRO',
      };
      return (
        <Badge 
          variant={currentRegime === 'red' ? 'destructive' : currentRegime === 'green' ? 'default' : 'outline'}
          className={currentRegime === 'green' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          {labels[currentRegime as string] || 'NEUTRO'}
        </Badge>
      );
    } else {
      const labels: Record<CSAlphaRegime, string> = {
        green: 'STRESS (COMPRA)',
        red: 'RISK-ON (VENDA)',
        neutral: 'NEUTRO',
      };
      const colors: Record<CSAlphaRegime, string> = {
        green: 'bg-green-600 hover:bg-green-700',
        red: '',
        neutral: '',
      };
      
      return (
        <Badge 
          variant={currentRegime === 'red' ? 'destructive' : currentRegime === 'neutral' ? 'outline' : 'default'}
          className={colors[currentRegime as CSAlphaRegime]}
        >
          {labels[currentRegime as CSAlphaRegime]}
        </Badge>
      );
    }
  };

  // Get indicator display name
  const indicatorName = indicator === 'nfci' ? 'NFCI' : indicator === 'ssrAlpha' ? 'SSR Alpha' : 'CS Alpha';
  const indicatorLabel = indicator === 'nfci' ? 'NFCI' : indicator === 'ssrAlpha' ? 'SSR RSI' : 'HY Spread';

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LineChart className="h-6 w-6" />
            Bitcoin & Financial Risk
          </h1>
          <p className="text-muted-foreground">
            BTC colorido pelo regime {indicatorName}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicator selector */}
          <Tabs value={indicator} onValueChange={(v) => setIndicator(v as IndicatorType)}>
            <TabsList>
              <TabsTrigger value="nfci">NFCI</TabsTrigger>
              <TabsTrigger value="csAlpha">CS Alpha</TabsTrigger>
              <TabsTrigger value="ssrAlpha">SSR Alpha</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Main Unified Chart - BTC + Indicator Histogram + Brush */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Bitcoin & {indicatorName}
              </CardTitle>
              {getRegimeBadge()}
              {currentIndicatorValue !== null && (
                <span className="text-sm text-muted-foreground font-mono">
                  {indicatorLabel}: {indicator === 'nfci' 
                    ? (currentIndicatorValue >= 0 ? '+' : '') + currentIndicatorValue.toFixed(3)
                    : indicator === 'ssrAlpha'
                    ? currentIndicatorValue.toFixed(1)
                    : currentIndicatorValue.toFixed(2) + '%'
                  }
                </span>
              )}
            </div>
            
            {/* Period selector */}
            <div className="flex gap-1">
              {PERIODS.map((p) => (
                <Button
                  key={p}
                  variant={period === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPeriod(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {refreshing ? (
            <div className="space-y-2">
              <Skeleton className="h-[350px] w-full" />
              <Skeleton className="h-[100px] w-full" />
              <Skeleton className="h-[50px] w-full" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[500px] flex items-center justify-center text-muted-foreground">
              Dados não disponíveis para o período selecionado
            </div>
          ) : (
            <div className="space-y-0">
              {/* BTC Price Chart */}
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={CHART_MARGIN} syncId="btc-indicator">
                    {/* Regime Background Zones - Green and Red only */}
                    {regimeZones.map((zone, i) => (
                      <ReferenceArea
                        key={`zone-${i}`}
                        x1={zone.startTs}
                        x2={zone.endTs}
                        yAxisId="btc"
                        fill={getRegimeColor(zone.regime, 0.2)}
                        fillOpacity={1}
                      />
                    ))}
                    
                    <XAxis
                      dataKey="ts"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tick={false}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      tickLine={false}
                      height={0}
                      ticks={xAxisTicks}
                    />
                    
                    {/* Y-axis: BTC Price (log scale) */}
                    <YAxis
                      yAxisId="btc"
                      orientation="right"
                      scale="log"
                      domain={['auto', 'auto']}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                      axisLine={false}
                      tickLine={false}
                      width={50}
                    />

                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          return (
                            <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
                              <p className="text-sm font-medium mb-2">
                                {format(parseISO(d.date), 'dd MMM yyyy', { locale: ptBR })}
                              </p>
                              <div className="space-y-1 text-sm">
                                <p className="text-orange-500">
                                  BTC: ${d.btcPrice?.toLocaleString()}
                                </p>
                                <p style={{ color: getRegimeColor(d.regime) === 'transparent' ? 'hsl(var(--muted-foreground))' : getRegimeColor(d.regime) }}>
                                  {indicatorLabel}: {indicator === 'nfci' 
                                    ? (d.indicatorValue >= 0 ? '+' : '') + d.indicatorValue?.toFixed(4)
                                    : indicator === 'ssrAlpha'
                                    ? d.indicatorValue?.toFixed(1)
                                    : d.indicatorValue?.toFixed(2) + '%'
                                  }
                                </p>
                                <p style={{ color: getRegimeColor(d.regime) === 'transparent' ? 'hsl(var(--muted-foreground))' : getRegimeColor(d.regime) }}>
                                  Regime: {
                                    indicator === 'nfci'
                                      ? (d.regime === 'green' ? 'Relaxamento' : d.regime === 'red' ? 'Aperto' : 'Neutro')
                                      : indicator === 'ssrAlpha'
                                      ? (d.regime === 'green' ? 'Sobrevendido' : d.regime === 'red' ? 'Sobrecomprado' : 'Neutro')
                                      : (d.regime === 'green' ? 'Stress (Compra)' : d.regime === 'red' ? 'Risk-on (Venda)' : 'Neutro')
                                  }
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />

                    {/* BTC Line */}
                    <Line
                      yAxisId="btc"
                      type="monotone"
                      dataKey="btcPrice"
                      name="Bitcoin (USD)"
                      stroke="hsl(25, 95%, 53%)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Separator */}
              <Separator className="my-0" />

              {/* Indicator Panel - Use Area chart for SSR Alpha (like SSRAlphaCard), Bar for others */}
              <div className="h-[150px]">
                <ResponsiveContainer width="100%" height="100%">
                  {indicator === 'ssrAlpha' ? (
                    // SSR Alpha uses Area chart with colored zones (matching SSRAlphaCard style)
                    <ComposedChart data={chartData} margin={{ top: 5, right: 60, left: 20, bottom: 0 }} syncId="btc-indicator">
                      <defs>
                        <linearGradient id="ssrRsiGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#fbbf24" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      
                      {/* Overbought zone background */}
                      <ReferenceArea
                        y1={ssrData?.config?.rsiOverbought ?? 75}
                        y2={100}
                        fill="rgba(239, 68, 68, 0.1)"
                        fillOpacity={1}
                      />
                      
                      {/* Oversold zone background */}
                      <ReferenceArea
                        y1={0}
                        y2={ssrData?.config?.rsiOversold ?? 35}
                        fill="rgba(16, 185, 129, 0.1)"
                        fillOpacity={1}
                      />
                      
                      <XAxis
                        dataKey="ts"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        axisLine={false}
                        tickLine={false}
                        ticks={xAxisTicks}
                      />
                      <YAxis
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        orientation="right"
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        axisLine={false}
                        tickLine={false}
                        width={35}
                      />
                      
                      {/* Reference lines */}
                      <ReferenceLine y={ssrData?.config?.rsiOverbought ?? 75} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.7} />
                      <ReferenceLine y={50} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" strokeOpacity={0.3} />
                      <ReferenceLine y={ssrData?.config?.rsiOversold ?? 25} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.7} />
                      
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const d = payload[0].payload;
                            return (
                              <div className="bg-popover/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
                                <p className="text-xs text-muted-foreground mb-1">
                                  {format(parseISO(d.date), 'dd MMM yyyy', { locale: ptBR })}
                                </p>
                                <p className="text-sm font-medium text-amber-400">
                                  SSR RSI: {d.indicatorValue?.toFixed(1)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {d.regime === 'green' ? 'Sobrevendido' : d.regime === 'red' ? 'Sobrecomprado' : 'Neutro'}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      
                      {/* RSI Area */}
                      <Area
                        type="monotone"
                        dataKey="indicatorValue"
                        stroke="#fbbf24"
                        strokeWidth={2}
                        fill="url(#ssrRsiGradient)"
                        name="SSR RSI"
                        connectNulls
                      />
                    </ComposedChart>
                  ) : (
                    // NFCI and CS Alpha use Bar chart
                    <BarChart data={chartData} margin={HISTOGRAM_MARGIN} syncId="btc-indicator">
                      <XAxis
                        dataKey="ts"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tick={false}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                        tickLine={false}
                        height={0}
                        ticks={xAxisTicks}
                      />
                      <YAxis
                        orientation="right"
                        tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => indicator === 'nfci' ? v.toFixed(1) : v.toFixed(1) + '%'}
                        axisLine={false}
                        tickLine={false}
                        width={50}
                      />
                      {indicator === 'nfci' && (
                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                      )}
                      {indicator === 'csAlpha' && (
                        <ReferenceLine y={macroData?.creditSpreadAlpha?.lowerThreshold ?? 1.5} stroke="hsl(0, 84%, 60%)" strokeDasharray="3 3" strokeOpacity={0.5} />
                      )}
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const d = payload[0].payload;
                            const regimeColor = getRegimeColor(d.regime);
                            const displayColor = regimeColor === 'transparent' ? 'hsl(var(--muted-foreground))' : regimeColor;
                            return (
                              <div className="bg-popover border border-border rounded-lg p-2 shadow-lg">
                                <p className="text-xs text-muted-foreground">
                                  {format(parseISO(d.date), 'dd MMM yyyy', { locale: ptBR })}
                                </p>
                                <p className="text-sm font-medium" style={{ color: displayColor }}>
                                  {indicatorLabel}: {indicator === 'nfci' 
                                    ? (d.indicatorValue >= 0 ? '+' : '') + d.indicatorValue?.toFixed(4)
                                    : d.indicatorValue?.toFixed(2) + '%'
                                  }
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {indicator === 'nfci'
                                    ? (d.regime === 'green' ? 'Relaxamento' : d.regime === 'red' ? 'Aperto' : 'Neutro')
                                    : (d.regime === 'green' ? 'Stress (Compra)' : d.regime === 'red' ? 'Risk-on (Venda)' : 'Neutro')
                                  }
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="indicatorValue" radius={[1, 1, 0, 0]}>
                        {chartData.map((entry, index) => {
                          const color = getRegimeColor(entry.regime);
                          // For neutral, use muted color instead of transparent for bars
                          const barColor = color === 'transparent' ? 'hsl(var(--muted-foreground))' : color;
                          return (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={barColor}
                              fillOpacity={color === 'transparent' ? 0.5 : 1}
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>

              {/* Brush for Period Adjustment */}
              <div className="h-[65px] pb-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ ...BRUSH_MARGIN, bottom: 10 }} syncId="btc-indicator">
                    <defs>
                      <linearGradient id="brushBtcGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="ts"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                      axisLine={{ stroke: 'hsl(var(--border))' }}
                      tickLine={false}
                      ticks={xAxisTicks}
                    />
                    <Area
                      type="monotone"
                      dataKey="btcPrice"
                      stroke="#f97316"
                      strokeWidth={1}
                      fill="url(#brushBtcGradient)"
                    />
                    <Brush
                      dataKey="ts"
                      height={40}
                      stroke="#f97316"
                      fill="hsl(215 20% 16%)"
                      travellerWidth={10}
                      tickFormatter={(ts) => format(new Date(ts), 'MMM yy', { locale: ptBR })}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          
          {/* Chart Legend - Dynamic based on indicator */}
          <div className="flex justify-center gap-6 mt-3 text-xs text-muted-foreground">
            {indicator === 'nfci' ? (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40" />
                  NFCI &lt; 0 = Relaxamento (favorável risco)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-red-500/20 border border-red-500/40" />
                  NFCI &gt; 0 = Aperto (desfavorável risco)
                </span>
              </>
            ) : indicator === 'ssrAlpha' ? (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40" />
                  RSI &lt; {ssrData?.config?.rsiOversold ?? 25} = Sobrevendido (Compra)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-red-500/20 border border-red-500/40" />
                  RSI &gt; {ssrData?.config?.rsiOverbought ?? 75} = Sobrecomprado (Venda)
                </span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40" />
                  Spread &gt; +2.3σ = Stress (Compra)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-red-500/20 border border-red-500/40" />
                  Spread &lt; 2.55% = Risk-on (Venda)
                </span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Indicator Description Card */}
      <Card className="bg-muted/30 border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4" />
            {indicatorInfo.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          {indicatorInfo.description.map((line, i) => (
            <div key={i} className="leading-relaxed prose prose-sm dark:prose-invert prose-strong:text-foreground prose-strong:font-semibold max-w-none">
              <ReactMarkdown>{line}</ReactMarkdown>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Last updated */}
      {macroData?.lastUpdated && (
        <p className="text-xs text-muted-foreground text-center">
          Última atualização: {format(new Date(macroData.lastUpdated), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
        </p>
      )}
    </div>
  );
}
